import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Form,
  Input,
  Button,
  Typography,
  Space,
  Tag,
  Modal,
  Descriptions,
  notification,
  Statistic,
} from "antd";
import {
  EyeOutlined,
  MailOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Text, Paragraph } = Typography;

const formatDateLabel = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("fr-FR");
};

const getStatusTag = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "read":
      return <Tag color="green">Lu</Tag>;
    case "email_error":
      return <Tag color="red">Email non envoye</Tag>;
    default:
      return <Tag color="blue">Nouveau</Tag>;
  }
};

const Contact = () => {
  const [form] = Form.useForm();
  const user = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "{}"),
    []
  );
  const isAdmin = user?.type === "admin";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const [settingsResponse, messagesResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/contact/settings`),
        axios.get(`${process.env.REACT_APP_API_URL}/contact/messages`),
      ]);

      form.setFieldsValue({
        recipientEmail: settingsResponse.data?.recipientEmail || "",
      });
      setMessages(
        Array.isArray(messagesResponse.data) ? messagesResponse.data : []
      );
    } catch (error) {
      notification.error({
        message: "Erreur de chargement",
        description:
          error?.response?.data?.message ||
          error.message ||
          "Impossible de charger les contacts.",
      });
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [fetchData, isAdmin]);

  const handleSaveSettings = async (values) => {
    setSaving(true);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/contact/settings`,
        values
      );

      form.setFieldsValue({
        recipientEmail: response.data?.recipientEmail || values.recipientEmail,
      });

      notification.success({
        message: "Email de reception mis a jour",
      });
    } catch (error) {
      notification.error({
        message: "Erreur de sauvegarde",
        description:
          error?.response?.data?.message ||
          error.message ||
          "Impossible de sauvegarder l'email de reception.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenMessage = async (record) => {
    setSelectedMessage(record);
    setIsModalVisible(true);

    if (String(record?.status || "").toLowerCase() !== "new") {
      return;
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/contact/messages/${record._id}/read`
      );
      const updatedMessage = response.data;

      setMessages((currentMessages) =>
        currentMessages.map((message) =>
          message._id === updatedMessage._id ? updatedMessage : message
        )
      );
      setSelectedMessage(updatedMessage);
    } catch (error) {
      notification.error({
        message: "Impossible de marquer le message comme lu",
        description:
          error?.response?.data?.message || error.message || "Veuillez reessayer.",
      });
    }
  };

  const unreadCount = messages.filter(
    (message) => String(message?.status || "").toLowerCase() === "new"
  ).length;

  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => formatDateLabel(value),
      sorter: (left, right) =>
        new Date(right?.createdAt || 0).getTime() -
        new Date(left?.createdAt || 0).getTime(),
      defaultSortOrder: "ascend",
    },
    {
      title: "Utilisateur",
      key: "senderName",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record?.userId?.username || record?.senderName || "-"}</Text>
          {record?.userId?.usernameInstagram ? (
            <Text type="secondary">{record.userId.usernameInstagram}</Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "senderEmail",
      key: "senderEmail",
      ellipsis: true,
    },
    {
      title: "Objet",
      dataIndex: "object",
      key: "object",
      ellipsis: true,
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
      filters: [
        { text: "Nouveau", value: "new" },
        { text: "Lu", value: "read" },
        { text: "Email non envoye", value: "email_error" },
      ],
      onFilter: (value, record) => String(record?.status || "") === value,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleOpenMessage(record)}
        >
          Details
        </Button>
      ),
    },
  ];

  if (!isAdmin) {
    return (
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card bordered={false}>
            <Text strong>Cette page est reservee aux administrateurs.</Text>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col xs={24} md={8}>
          <Card bordered={false}>
            <Statistic title="Messages recus" value={messages.length} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card bordered={false}>
            <Statistic title="Nouveaux messages" value={unreadCount} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card bordered={false}>
            <Statistic
              title="Dernier message"
              value={messages[0] ? formatDateLabel(messages[0].createdAt) : "-"}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={8}>
          <Card
            bordered={false}
            title="Email de reception"
            extra={<MailOutlined />}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSaveSettings}
              initialValues={{ recipientEmail: "" }}
            >
              <Form.Item
                name="recipientEmail"
                label="Email de reception des messages utilisateurs"
                rules={[
                  {
                    required: true,
                    message: "Veuillez renseigner un email de reception",
                  },
                  {
                    type: "email",
                    message: "Veuillez saisir un email valide",
                  },
                ]}
              >
                <Input placeholder="contact@trend-me.com" />
              </Form.Item>

              <Paragraph type="secondary" style={{ marginTop: -8 }}>
                Les messages envoyes depuis le formulaire contact de l'application
                mobile seront transmis a cette adresse et conserves dans ce tableau.
              </Paragraph>

              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                icon={<SaveOutlined />}
              >
                Enregistrer
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card bordered={false} title="Messages de contact">
            <Table
              rowKey="_id"
              loading={loading}
              columns={columns}
              dataSource={messages}
              pagination={{ pageSize: 8, showSizeChanger: false }}
              locale={{ emptyText: "Aucun message de contact" }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title="Detail du message"
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedMessage(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsModalVisible(false);
              setSelectedMessage(null);
            }}
          >
            Fermer
          </Button>,
        ]}
      >
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Objet">
            {selectedMessage?.object || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Utilisateur app">
            {selectedMessage?.userId?.username || selectedMessage?.senderName || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Instagram">
            {selectedMessage?.userId?.usernameInstagram || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Email expediteur">
            {selectedMessage?.senderEmail || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Email de reception">
            {selectedMessage?.recipientEmail || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Date">
            {formatDateLabel(selectedMessage?.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label="Statut">
            {getStatusTag(selectedMessage?.status)}
          </Descriptions.Item>
        </Descriptions>

        <Card size="small" style={{ marginTop: 16 }}>
          <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
            {selectedMessage?.messageContent || "-"}
          </Paragraph>
        </Card>
      </Modal>
    </>
  );
};

export default Contact;
